import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiLoader, FiZoomIn, FiZoomOut } from 'react-icons/fi';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { classifyPdfError, loadPdfDocument, renderPdfPage } from '../../utils/pdfPreview';
import type { PdfErrorKind } from '../../utils/pdfPreview';

const ZOOM_STEPS = [0.6, 0.8, 1, 1.25, 1.5, 2, 3];
const BASE_PAGE_WIDTH = 820;

/** One page, rasterised only when it scrolls near the viewport. */
const PdfPage: React.FC<{
  doc: PDFDocumentProxy;
  pageNumber: number;
  width: number;
  onVisible: (pageNumber: number) => void;
}> = ({ doc, pageNumber, width, onVisible }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [inView, setInView] = useState(pageNumber === 1);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            // Track the page currently dominating the viewport.
            if (entry.intersectionRatio > 0.5) onVisible(pageNumber);
          }
        }
      },
      { rootMargin: '400px', threshold: [0, 0.5, 1] },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [pageNumber, onVisible]);

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    let task: { cancel: () => void } | null = null;

    (async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      try {
        await renderPdfPage(doc, pageNumber, canvas, width, {
          onTask: (t) => {
            task = t;
          },
        });
        if (!cancelled) setRendered(true);
      } catch {
        /* Cancellation, or a single bad page — neither should break the reader. */
      }
    })();

    return () => {
      // Two concurrent renders on one canvas deadlock pdf.js.
      cancelled = true;
      task?.cancel();
    };
  }, [doc, pageNumber, width, inView]);

  return (
    <div
      ref={wrapperRef}
      className="relative mx-auto mb-4 bg-white rounded-lg shadow-xl overflow-hidden"
      style={{ width, minHeight: rendered ? undefined : width * 1.29 }}
    >
      <canvas ref={canvasRef} className="block w-full" />
      {!rendered && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#f4f4f5]">
          <FiLoader size={20} className="text-zinc-400 animate-spin" />
        </div>
      )}
      <span className="absolute bottom-2 right-2 text-[10px] font-semibold text-white bg-black/50 px-2 py-0.5 rounded-full">
        {pageNumber}
      </span>
    </div>
  );
};

interface PdfReaderProps {
  url: string;
  /** Rendered when the document can't be parsed; receives why it failed. */
  fallback: (kind: PdfErrorKind) => React.ReactNode;
}

/** Scrollable, zoomable PDF reader used inside the fullscreen viewer. */
export const PdfReader: React.FC<PdfReaderProps> = ({ url, fallback }) => {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorKind, setErrorKind] = useState<PdfErrorKind>('network');
  const [zoomIndex, setZoomIndex] = useState(2);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    loadPdfDocument(url)
      .then((loaded) => {
        if (cancelled) return;
        setDoc(loaded);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setErrorKind(classifyPdfError(err));
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  const handleVisible = useCallback((page: number) => setCurrentPage(page), []);

  if (status === 'error') return <>{fallback(errorKind)}</>;

  if (status === 'loading' || !doc) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 bg-[#1C1C1E] gap-3">
        <FiLoader size={26} className="text-zinc-400 animate-spin" />
        <p className="text-zinc-400 text-xs font-medium">Loading document…</p>
      </div>
    );
  }

  const zoom = ZOOM_STEPS[zoomIndex];
  const pageWidth = Math.round(BASE_PAGE_WIDTH * zoom);

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-[#2A2A2D]">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#232326] border-b border-[#3A3A3C] shrink-0">
        <span className="text-zinc-300 text-xs font-semibold tabular-nums">
          Page {currentPage} of {doc.numPages}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
            disabled={zoomIndex === 0}
            className="p-1.5 rounded-md text-zinc-300 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors cursor-pointer"
            title="Zoom out"
          >
            <FiZoomOut size={15} />
          </button>
          <span className="text-zinc-400 text-[11px] font-semibold w-11 text-center tabular-nums">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() => setZoomIndex((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
            disabled={zoomIndex === ZOOM_STEPS.length - 1}
            className="p-1.5 rounded-md text-zinc-300 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors cursor-pointer"
            title="Zoom in"
          >
            <FiZoomIn size={15} />
          </button>
        </div>
      </div>

      {/* Pages */}
      <div className="flex-1 overflow-auto p-4">
        {Array.from({ length: doc.numPages }, (_, i) => (
          <PdfPage
            key={i + 1}
            doc={doc}
            pageNumber={i + 1}
            width={pageWidth}
            onVisible={handleVisible}
          />
        ))}
      </div>
    </div>
  );
};
